<template>
  <div class="md-layout">
    <CSLoading :is-loading="isLoading"></CSLoading>
    <div class="md-layout-item md-size-100">
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-green">
          <div class="card-icon">
            <md-icon>shelves</md-icon>
          </div>
          <h4 class="title">{{ $t("wms.warehouses") }}</h4>
          <md-button
            v-if="hasPermission('Write', 'Warehouses', 'IncomingGoods') || hasPermission('Write', 'Warehouses', 'Movement') || hasPermission('Write', 'Warehouses', 'ManualAdjustment')"
            class="md-icon-button md-success add-button"
            @click="showAddModal"
          >
            <md-icon>add</md-icon>
          </md-button>
        </md-card-header>
        <md-card-content>
          <div class="md-layout">
            <div class="md-layout-item md-size-25">
              <md-field md-clearable>
                <label>{{ $t("wms.keywords") }}</label>
                <md-input v-model="filter.keywords" @input="debounceApplyFilters"></md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-25">
              <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 0">
                <label>{{ $t("wms.location") }}</label>
                <v-select :options="allLocationOptions" :placeholder="$t('wms.location')" @input="handleSelectLocation" class="custom-select"> </v-select>
              </div>
            </div>
            <div class="md-layout-item md-size-25">
              <md-field md-clearable>
                <label>{{ $t("wms.warehouse") }}</label>
                <md-select v-model="filter.warehouseId" name="warehouseId" id="warehouseId" :disabled="isWarehouseAssigned" @md-selected="debounceApplyFilters">
                  <md-option :key="i.id" v-for="i in warehouseOptions" :value="i.id">{{ i.name }}</md-option>
                </md-select>
              </md-field>
            </div>
          </div>
          <div class="md-layout">
            <div class="md-layout-item md-size-25">
              <md-field md-clearable>
                <label>{{ $t("wms.sku") }}</label>
                <md-input v-model="filter.sku" @input="debounceApplyFilters"></md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-25">
              {{ $t("wms.exactMatch") }}
              <md-switch v-model="filterExactMatchSku" />
            </div>
          </div>
          <div class="md-layout">
            <div class="md-lay-out-item md-size-25">
              <md-button v-if="hasPermission('Write', 'Warehouses', 'Export')" @click="exportWarehouses" class="md-success">{{ $t("wms.export") }}</md-button>
            </div>
          </div>
          <div class="md-table table-sticky" table-header-color="green">
            <v-server-table :columns="tableColumns" :options="tableOptions" ref="table" class="table-warehouses" id="table-warehouses">
              <template #actions="{ row }">
                <div class="md-table-cell-container cell-actions">
                  <md-button class="md-icon-button" v-if="hasPermission('Read', 'Inventories', '')" @click="showHistoryModal(row.sku)">
                    <md-icon>history</md-icon>
                  </md-button>
                  <md-button class="md-icon-button" v-if="isAdminUser || hasPermission('Write', 'Warehouses', 'Edit')" @click="showEditModal(row)">
                    <md-icon>edit</md-icon>
                  </md-button>
                </div>
              </template>
              <template #warehouse="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'StorageLocation')" class="cell-warehouse">
                  {{ row.warehouseName }}
                </div>
              </template>
              <template #location="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'StorageLocation')" class="cell-location">{{ row.warehouseCode }}-{{ row.storageLocation }}</div>
              </template>
              <template #sku="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Sku')" class="cell-sku">
                  {{ row.sku }}
                </div>
              </template>
              <template #name="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Name')" class="cell-name">
                  {{ row.name }}
                </div>
              </template>
              <template #inventory="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Inventory')" class="cell-inventory">
                  {{ row.inventory }}
                </div>
              </template>
              <template #reserved="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Reserved')" class="cell-inventory">
                  {{ row.reserved }}
                </div>
              </template>
              <template #date="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Date')" class="cell-date">
                  {{ row.date | formatDateTime }}
                </div>
              </template>
              <template #h__purchasePrice>
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'PurchasePrice')" class="head-purchase-price">
                  {{ $t("wms.table.purchasePrice") }}
                  <md-icon class="md-size-0.5">info</md-icon>
                  <md-tooltip md-direction="top">{{ $t("wms.table.purchasePriceTip1") }} <br />{{ $t("wms.table.purchasePriceTip2") }} <br />{{ $t("wms.table.purchasePriceTip3") }}</md-tooltip>
                </div>
              </template>
              <template #purchasePrice="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'PurchasePrice')" class="cell-purchase-price">
                  <div>
                    EK: {{ row.purchasePrice | formatUnit("USD", 2, 2) }}
                    <span v-if="hasPermission('LimitRead', 'Warehouses', 'ExchangeRate')" style="color: #555; font-size: 0.95em; margin-left: 8px">
                      ({{ row.exchangeRate | formatUnit("", 3, 3) }})
                    </span>
                  </div>
                  <div style="margin-top: 4px">
                    BZNK:
                    <span v-if="hasPermission('LimitRead', 'Warehouses', 'AdditionalCosts')">
                      {{ row.additionalCosts | formatUnit("EUR", 2, 2) }}
                    </span>
                    <span v-if="hasPermission('LimitRead', 'Warehouses', 'Customs')"> &nbsp;+&nbsp;{{ (row.customsItem || 0) | formatUnit("EUR", 2, 2) }}</span>
                  </div>
                  <div style="margin-top: 4px">BEP: {{ calcValueOfGoods(row) | formatUnit("EUR", 2, 2) }}</div>
                  <div style="margin-top: 4px; font-weight: bold; border-top: 1px solid #ccc">POS: {{ ((row.inventory + row.reserved) * calcValueOfGoods(row)) | formatUnit("EUR", 2, 2) }}</div>
                </div>
              </template>
              <template #additionalCosts="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'AdditionalCosts')" class="cell-additional-costs">{{ row.additionalCosts | formatUnit("EUR", 2, 2) }}</div>
              </template>
              <template #exchangeRate="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'ExchangeRate')" class="cell-exchangeRate">
                  {{ row.exchangeRate }}
                </div>
              </template>
              <template #valueOfGoods="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'PurchasePrice') && hasPermission('LimitRead', 'Warehouses', 'AdditionalCosts')" class="cell-value-of-goods">
                  {{ calcValueOfGoods(row) }} EUR
                </div>
              </template>
              <template #h__reference>
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Reference')" class="head-reference">
                  {{ $t("wms.table.reference") }} <md-icon>info</md-icon>
                  <md-tooltip md-direction="top"
                    >{{ $t("wms.table.source") }} <br />
                    {{ $t("wms.table.reference") }}</md-tooltip
                  >
                </div>
              </template>
              <template #reference="{ row }">
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Source')" class="cell-source">
                  {{ row.source || "-" }}
                </div>
                <div v-if="hasPermission('LimitRead', 'Warehouses', 'Reference')" class="cell-reference">
                  {{ row.reference || "-" }}
                </div>
              </template>
            </v-server-table>
          </div>
        </md-card-content>
      </md-card>
    </div>
    <modal v-if="editModal" @close="hideEditModal" class-name="warehouse-modal-container">
      <template slot="header">
        <h4 class="modal-title">
          {{ form.id > 0 ? $t("wms.editWarehouse") : $t("wms.addWarehouse") }}
        </h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideEditModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <ValidationObserver v-slot="{}" ref="editForm">
          <md-field>
            <label>{{ $t("wms.sku") }}</label>
            <md-input v-model="form.sku" type="text" disabled> </md-input>
          </md-field>
          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-50">
              <md-field>
                <label>{{ $t("wms.warehouses") }}</label>
                <md-select v-model="form.warehouseId" name="warehouseId" id="warehouseId" disabled>
                  <md-option :key="i.id" v-for="i in warehouseOptions" :value="i.id">{{ i.name }}</md-option>
                </md-select>
              </md-field>
            </div>
            <div class="md-layout-item md-size-50">
              <ValidationProvider name="storageLocationId" rules="required" v-slot="{ errors }">
                <md-field>
                  <label>{{ $t("wms.location") }}</label>
                  <md-select v-model="form.storageLocationId" name="storageLocationId" id="storageLocationId" disabled>
                    <md-option :key="i.id" v-for="i in locationOptions" :value="i.id">{{ i.fullName }}</md-option>
                  </md-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-50">
              <md-field>
                <label>{{ $t("wms.inventory") }}</label>
                <md-input v-model="form.inventory" type="number"> </md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-50">
              <md-field>
                <label>{{ $t("wms.reserved") }}</label>
                <md-input v-model="form.reserved" type="number"> </md-input>
              </md-field>
            </div>
          </div>
          <div class="md-field md-has-value">
            <label>{{ $t("wms.inboundDate") }}</label>
            <date-time-picker :startDate="form.date" :singleDate="true" @onChange="(d) => (form.date = d.startDate)" class="datetime-input" timeFormat="HH:mm" />
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-50">
              <md-field :class="{ 'md-has-value': form.purchasePrice !== null }">
                <label>{{ $t("wms.purchasePrice") }}</label>
                <CurrencyInput class="md-input" v-model="form.purchasePrice" :options="{ currency: 'USD' }" />

                <span class="md-suffix">USD</span>
              </md-field>
            </div>
            <div class="md-layout-item md-size-50">
              <md-field :class="{ 'md-has-value': form.exchangeRate !== null }">
                <label>{{ $t("wms.exchangeRate") }}</label>
                <CurrencyInput class="md-input" v-model="form.exchangeRate" :options="{ currency: 'USD', precision: 3 }" />
                <span class="md-suffix">USD</span>
              </md-field>
            </div>
          </div>

          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-50">
              <md-field :class="{ 'md-has-value': form.additionalCosts !== null }">
                <label>{{ $t("wms.additionalCosts") }}</label>
                <CurrencyInput class="md-input" v-model="form.additionalCosts" :options="{ currency: 'EUR' }" />
                <span class="md-suffix">EUR</span>
              </md-field>
            </div>
            <div class="md-layout-item md-size-50">
              <md-field :class="{ 'md-has-value': form.customsItem !== null }">
                <label>{{ $t("wms.customsItem") }}</label>
                <CurrencyInput class="md-input" v-model="form.customsItem" :options="{ currency: 'EUR' }" />
                <span class="md-suffix">EUR</span>
              </md-field>
            </div>
          </div>
          <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-50">
              <md-field>
                <label>{{ $t("wms.source") }}</label>
                <md-input v-model="form.source" type="text"> </md-input>
              </md-field>
            </div>
            <div class="md-layout-item md-size-50">
              <md-field>
                <label>{{ $t("wms.reference") }}</label>
                <md-input v-model="form.reference" type="text"> </md-input>
              </md-field>
            </div>
          </div>

          <!-- <ValidationProvider name="form.historyTypeId" rules="required" v-slot="{ errors }">
            <md-field>
              <label>{{ $t("wms.historyType") }}</label>
              <md-select v-model="form.historyTypeId" name="form.historyTypeId" id="form.historyTypeId">
                <md-option :key="i.id" v-for="i in historyTypeOptions" :value="i.id">{{ i.name }}</md-option>
              </md-select>
              <div class="md-error" v-if="errors[0]">
                {{ $t(errors[0]) }}
              </div>
            </md-field>
          </ValidationProvider> -->
        </ValidationObserver>
      </template>
      <template slot="footer">
        <md-button class="md-secondary mr-1" @click="hideEditModal">{{ $t("wms.close") }}</md-button>
        <md-button class="md-success" @click="editWarehouse">{{ $t("wms.save") }}</md-button>
      </template>
    </modal>

    <modal v-if="addModal" @close="hideAddModal" class-name="add-warehouse-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("wms.addWarehouse") }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideAddModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <tabs
          :tab-name="[$t('orders.incomingGoods'), $t('orders.movementOfGoods'), $t('orders.manualAdjustment')]"
          v-on:panel-changed="() => alert('changed')"
          color-button="success"
          content-flex-direction-column
        >
          <template slot="tab-pane-1">
            <ValidationObserver v-slot="{}" ref="incomingGoodsForm">
              <ValidationProvider name="form.sku" rules="required" v-slot="{ errors }">
                <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                  <label>{{ $t("wms.sku") }}</label>
                  <v-select v-model="form.skuObject" label="sku" :options="skuOptions" :filter="fuseSearchSKU" :placeholder="$t('wms.sku')" class="custom-select">
                    <template slot="no-options"> {{ $t("noRecord") }} </template>

                    <template slot="option" slot-scope="option">
                      <div :class="`d-center ${option.exact ? 'exact' : ''}`" v-html="option.sku"></div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center" v-html="option.sku"></div>
                    </template>
                  </v-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </div>
              </ValidationProvider>
              <ValidationProvider name="form.inventory" :rules="{ required: true, min_value: 0 }" v-slot="{ errors }">
                <md-field>
                  <label>{{ $t("wms.inventory") }}</label>
                  <md-input v-model="form.inventory" type="number" min="0"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
              <ValidationProvider name="form.storageLocation" rules="required" v-slot="{ errors }">
                <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                  <label>{{ $t("wms.location") }}</label>
                  <v-select v-model="form.storageLocation" :options="allLocationOptions" :placeholder="$t('wms.location')" class="custom-select"> </v-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </div>
              </ValidationProvider>

              <ValidationProvider name="form.reason" :rules="{ required: true }" v-slot="{ errors }">
                <md-field>
                  <label>{{ $t("wms.reason") }}</label>
                  <md-input v-model="form.reason"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
            </ValidationObserver>
            <div style="margin-top: 15px">
              <md-button class="md-secondary mr-1" @click="hideAddModal">{{ $t("wms.close") }}</md-button>
              <md-button v-if="hasPermission('Write', 'Warehouses', 'IncomingGoods')" class="md-success" @click="addIncomingGoods">{{ $t("wms.save") }}</md-button>
            </div>
          </template>
          <template slot="tab-pane-2">
            <ValidationObserver v-slot="{}" ref="movementOfGoodsForm">
              <ValidationProvider name="form.warehouseInventory" rules="required" v-slot="{ errors }">
                <div class="wrap-v-select custom-wrap-v-select" style="text-align: left">
                  <label>{{ $t("wms.inventory") }}</label>
                  <v-select v-model="form.warehouseInventory" :filterable="false" label="name" :options="options" @search="onSearchInvetories" :placeholder="$t('wms.inventory')" class="custom-select">
                    <template slot="no-options"> {{ $t("noRecord") }} </template>
                    <template slot="option" slot-scope="option">
                      <div :class="`d-center ${option.exact ? 'exact' : ''}`" v-html="option.name"></div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center" v-html="option.name"></div>
                    </template>
                  </v-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </div>
              </ValidationProvider>

              <md-field>
                <label>{{ $t("wms.quantity") }}</label>
                <md-input v-model="form.quantity" type="number"> </md-input>
              </md-field>

              <ValidationProvider name="form.toStorageLocation" rules="required" v-slot="{ errors }">
                <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 15px">
                  <label>{{ $t("wms.location") }}</label>
                  <v-select v-model="form.toStorageLocation" :options="allLocationOptions" :placeholder="$t('wms.location')" class="custom-select"> </v-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </div>
              </ValidationProvider>
              <!-- <md-field>
                <label>{{ $t("wms.reference") }}</label>
                <md-input v-model="form.reference" type="text"> </md-input>
              </md-field> -->
            </ValidationObserver>
            <div style="margin-top: 15px">
              <md-button class="md-secondary mr-1" @click="hideAddModal">{{ $t("wms.close") }}</md-button>
              <md-button v-if="hasPermission('Write', 'Warehouses', 'Movement')" class="md-success" @click="addMovementOfGoods">{{ $t("wms.save") }}</md-button>
            </div>
          </template>
          <template slot="tab-pane-3">
            <ValidationObserver v-slot="{}" ref="manualAdjustmentForm">
              <md-field>
                <label>{{ $t("wms.historyType") }}</label>
                <md-select v-model="form.historyType">
                  <md-option value="Removal For Spare Parts"> {{ getI18N("wms.statuses.historyTypes", "Removal For Spare Parts") }}</md-option>
                  <md-option value="Defect">{{ getI18N("wms.statuses.historyTypes", "Defect") }}</md-option>
                  <md-option value="Manual Adjustment">{{ getI18N("wms.statuses.historyTypes", "Manual Adjustment") }}</md-option>
                  <md-option value="Counting">{{ getI18N("wms.statuses.historyTypes", "Counting") }}</md-option>
                </md-select>
              </md-field>

              <ValidationProvider name="form.reason" :rules="{ required: true }" v-slot="{ errors }">
                <md-field>
                  <label>{{ $t("wms.reason") }}</label>
                  <md-input v-model="form.reason"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>

              <ValidationProvider name="form.warehouseInventory" rules="required" v-slot="{ errors }">
                <div class="wrap-v-select custom-wrap-v-select" style="text-align: left">
                  <label>{{ $t("wms.inventory") }}</label>
                  <v-select :filterable="false" @search="onSearchInvetories" label="name" class="custom-select" :options="options" v-model="form.warehouseInventory" :placeholder="$t('wms.inventory')">
                    <template slot="no-options">{{ $t("noRecord") }} </template>
                    <template slot="option" slot-scope="option">
                      <div :class="`d-center ${option.exact ? 'exact' : ''}`" v-html="option.name"></div>
                    </template>
                    <template slot="selected-option" slot-scope="option">
                      <div class="selected d-center" v-html="option.name"></div>
                    </template>
                  </v-select>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </div>
              </ValidationProvider>
              <ValidationProvider name="form.quantity" :rules="{ required: true, min_value: 0 }" v-slot="{ errors }">
                <md-field>
                  <label>{{ $t("wms.quantity") }}</label>
                  <md-input v-model="form.quantity" type="number" min="0"> </md-input>
                  <div class="md-error" v-if="errors[0]">
                    {{ $t(errors[0]) }}
                  </div>
                </md-field>
              </ValidationProvider>
              <md-switch v-model="form.isAdd" class="md-primary">{{ form.isAdd ? $t("wms.add") : $t("wms.subtract") }}</md-switch>
            </ValidationObserver>
            <div style="margin-top: 15px">
              <md-button class="md-secondary mr-1" @click="hideAddModal">{{ $t("wms.close") }}</md-button>
              <md-button v-if="hasPermission('Write', 'Warehouses', 'ManualAdjustment')" class="md-success" @click="addManualAdjustment">{{ $t("wms.save") }}</md-button>
            </div>
          </template>
        </tabs>
      </template>
      <template slot="footer"> </template>
    </modal>

    <modal v-if="historyModal" @close="hideHistoryModal" class-name="historical-modal-container">
      <template slot="header">
        <h4 class="modal-title">{{ $t("wms.history") }}: {{ sku }}</h4>
        <md-button class="md-simple md-just-icon md-round modal-default-button" @click="hideHistoryModal">
          <md-icon>clear</md-icon>
        </md-button>
      </template>

      <template slot="body">
        <div style="height: calc(100vh - 150px); overflow-x: auto">
          <div style="display: flex; justify-content: flex-start; margin-bottom: 20px">
            <div class="">
              <p class="date-title">{{ $t("wms.historyFromDateTime") }}</p>
              <simple-date-time-picker :id="`historyFromDateTime`" @datetimeChanged="(d) => dateFilterChanged(d, 'historyFromDateTime')" @clearDatetime="() => clearDatetime('historyFromDateTime')" />
            </div>
            <div style="margin-left: 20px">
              <p class="date-title">{{ $t("wms.historyToDateTime") }}</p>
              <simple-date-time-picker :id="`historyToDateTime`" @datetimeChanged="(d) => dateFilterChanged(d, 'historyToDateTime')" @clearDatetime="() => clearDatetime('historyToDateTime')" />
            </div>
          </div>
          <div style="display: flex; justify-content: flex-start; margin-bottom: 20px">
            <div style="min-width: 200px">
              <div class="wrap-v-select custom-wrap-v-select" style="text-align: left; margin-bottom: 0">
                <label>{{ $t("wms.location") }}</label>
                <v-select :options="allLocationOptions" :placeholder="$t('wms.location')" @input="handleSelectHistoryLocation" class="custom-select"> </v-select>
              </div>
            </div>
            <div style="margin-left: 20px">
              <md-field md-clearable>
                <label>{{ $t("wms.changeType") }}</label>
                <md-select v-model="historyFilters.changeType" name="filters.changeType" id="filters.changeType" @md-selected="debounceApplyFiltersHistory">
                  <md-option key="All" value="">{{ $t("wms.all") }}</md-option>
                  <md-option key="Add" value="Add">{{ $t("wms.add") }}</md-option>
                  <md-option key="Subtract" value="Subtract">{{ $t("wms.subtract") }}</md-option>
                </md-select>
              </md-field>
            </div>
            <div style="margin-left: 20px">
              <md-field md-clearable>
                <label>{{ $t("wms.historyType") }}</label>
                <md-select v-model="historyFilters.historyTypeIds" multiple name="filter.historyTypeId" id="filter.historyTypeId" @md-selected="debounceApplyFiltersHistory">
                  <md-option :key="i.id" v-for="i in historyTypeOptions" :value="i.id">{{ getI18N("wms.statuses.historyTypes", i.name) }}</md-option>
                </md-select>
              </md-field>
            </div>
          </div>
          <div style="display: flex; justify-content: flex-start; margin-bottom: 20px">
            <div style="">
              <md-field md-clearable>
                <label>{{ $t("wms.reason") }}</label>
                <md-input v-model="historyFilters.reason" @input="debounceApplyFiltersHistory"></md-input>
              </md-field>
            </div>
            <div style="margin-left: 20px">
              <md-field md-clearable>
                <label>{{ $t("wms.changedBy") }}</label>
                <md-input v-model="historyFilters.changeBy" @input="debounceApplyFiltersHistory"></md-input>
              </md-field>
            </div>
          </div>
          <div class="table-sticky">
            <v-server-table :columns="historyTableColumns" :options="historyTableOptions" ref="historyTable" class="table-warehouses-history" id="table-warehouses-history">
              <template #warehouseName="{ row }">{{ row.storageLocation && row.storageLocation.warehouseName }}</template>
              <template #dateTime="{ row }">
                {{ row.dateTime | formatDateTime }}
              </template>
              <template #location="{ row }">
                {{ row.storageLocation && row.storageLocation.fullName }}
              </template>
              <template #changedType="{ row }">
                <span v-if="row.changedType === 'Add'">
                  <span class="material-symbols-outlined">add</span>
                </span>
                <span v-else-if="row.changedType === 'Substract' || row.changedType === 'Subtract'">
                  <span class="material-symbols-outlined">remove</span>
                </span>
                <span v-else>
                  {{ row.changedType }}
                </span>
              </template>
              <template #historyTypeName="{ row }">
                {{ getI18N("wms.statuses.historyTypes", row.historyTypeName) }}
              </template>
              <template #changedBy="{ row }">
                {{ row.userWhoChangeName ? row.userWhoChangeName : "OCC" }}
              </template>
            </v-server-table>
          </div>
        </div>
      </template>
    </modal>
  </div>
</template>
<script>
import { debounce } from "lodash";
import { DateTimePicker } from "@lazy-copilot/datetimepicker";
import SimpleDateTimePicker from "@/components/DateTimePicker/SimpleDateTimePicker.vue";
import CSLoading from "../../components/Loading/CSLoading.vue";
import { Modal, Tabs } from "@/components";
import axiosFactory from "../../services/axios.factory";
import stickyTableHeader from "@/helpers/stickyTableHeader";
import { hasPermission, assignedWarehouse, isAdmin, canView } from "../../mixins/auth";
import { normalizeStatusName } from "../../mixins/formats";
import { EventBus } from "@/helpers/event-bus";
import Fuse from "fuse.js";
import { formatISO, format } from "date-fns";
// import VueNumeric from "vue-numeric";
import CurrencyInput from "@/components/Inputs/CurrencyInput.vue";
export default {
  components: {
    CSLoading,
    Modal,
    Tabs,
    CurrencyInput,
    SimpleDateTimePicker,
    DateTimePicker,
    // VueNumeric,
  },
  data() {
    const userWarehouse = assignedWarehouse();
    return {
      isLoading: false,
      addModal: false,
      editModal: false,
      historyModal: false,
      form: {},
      filter: {
        keywords: null,
        warehouseId: userWarehouse ? userWarehouse.id : null,
        sku: null,
      },
      filterExactMatchSku: false,
      sku: null,
      historyFilters: {
        storageLocationId: null,
        changeType: "",
        changeBy: "",
        reason: "",
        historyTypeId: 11,
        historyTypeIds: [],
        location: "",
      },
      tableColumns: ["actions", "warehouse", "location", "sku", "name", "inventory", "reserved", "date", "purchasePrice", "reference"], //"additionalCosts", "exchangeRate",
      tableOptions: {
        headings: {
          actions: () => this.$t("wms.table.actions"),
          warehouse: () => this.$t("wms.table.warehouse"),
          location: () => this.$t("wms.table.location"),
          sku: () => this.$t("wms.table.sku"),
          name: () => this.$t("wms.table.name"),
          inventory: () => this.$t("wms.table.inventory"),
          reserved: () => this.$t("wms.table.reserved"),
          date: () => this.$t("wms.table.date"),
          purchasePrice: () => this.$t("wms.table.purchasePrice"),
          additionalCosts: () => this.$t("wms.table.additionalCosts"),
          exchangeRate: () => this.$t("wms.table.exchangeRate"),
          valueOfGoods: () => this.$t("wms.table.valueOfGoods"),
          reference: () => this.$t("wms.table.reference"),
        },
        columnsClasses: {
          purchasePrice: "text-left",
        },
        perPage: 100,
        perPageValues: [100, 200, 500, 1000],
        filterable: false,
        sortIcon: {
          is: "fa-sort",
          base: "fa",
          up: "fa-sort-up",
          down: "fa-sort-down",
        },
        sortable: ["location", "date"],
        params: {},
        resizableColumns: false,
        requestFunction: this.requestFunction,
        responseAdapter: this.responseFunction,
        rowClassCallback: this.rowClassCallback,
        pagination: {
          show: true,
        },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("table.noRecord"),
        },
      },
      tableData: [],
      totalItems: 0,
      table: null,
      locationOptions: [],
      options: [],
      locations: [],
      products: [],

      historyTable: null,
      historyTableColumns: ["warehouseName", "dateTime", "location", "inStockOld", "inStockNew", "quantity", "totalOld", "totalNew", "changedType", "reason", "changedBy", "historyTypeName"],
      historyTableOptions: {
        headings: {
          warehouseName: () => this.$t("wms.warehouseName"),
          dateTime: () => this.$t("wms.dateTime"),
          location: () => this.$t("wms.location"),
          inStockOld: () => this.$t("wms.inStockOld"),
          inStockNew: () => this.$t("wms.inStockNew"),
          quantity: () => this.$t("wms.quantity"),
          totalOld: () => this.$t("wms.totalOld"),
          totalNew: () => this.$t("wms.totalNew"),
          changedType: () => this.$t("wms.changedType"),
          reason: () => this.$t("wms.reason"),
          changedBy: () => this.$t("wms.changedBy"),
          historyTypeName: () => this.$t("wms.historyType"),
        },
        perPage: 16,
        perPageValues: [16],
        filterable: false,
        sortable: [],
        params: {},
        resizableColumns: false,
        requestFunction: this.historyRequestFunction,
        responseAdapter: this.historyResponseFunction,
        rowClassCallback: this.rowClassCallback,
        pagination: {
          show: true,
        },
        texts: {
          count: this.$t("table.count"),
          limit: this.$t("table.records"),
          noResults: this.$t("table.noRecord"),
        },
      },
    };
  },
  computed: {
    warehouseOptions() {
      return this.$store.state.warehouses.warehouses || [];
    },
    historyTypeOptions() {
      return this.$store.state.warehouses.historyTypes || [];
    },
    skuOptions() {
      return this.products.map((p) => ({ value: p.sku, sku: p.sku, name: p.name })) || [];
    },
    allLocationOptions() {
      return (
        this.locations.map((p) => ({
          value: p.id,
          label: p.fullName,
        })) || []
      );
    },
    isWarehouseAssigned() {
      return !!assignedWarehouse();
    },
    isAdminUser() {
      return isAdmin();
    },
  },
  watch: {
    filterExactMatchSku(boolValue) {
      if (boolValue) {
        this.filter.exactMatchSku = true;
      } else {
        this.filter.exactMatchSku = false;
      }

      this.debounceApplyFilters();
    },
  },
  methods: {
    resetForm() {
      this.form = {};
      this.options = [];
      // this.form.warehouseInventory = null;
      // this.form.quantity = null;
      // this.form.storageLocation = null;
      // this.form.reason = null;
    },
    showAddModal() {
      this.form = { id: 0 };
      this.form.date = new Date();
      this.addModal = true;
    },
    hideAddModal() {
      this.addModal = false;
      this.resetForm();
    },
    showEditModal(item) {
      // Object.assign(this.form, item);
      this.form = { ...item };
      this.form.date = item.date ? new Date(item.date) : null;
      this.$store
        .dispatch("warehouses/getLocationsByWarehouse", {
          warehouseId: item.warehouseId,
          location: "",
        })
        .then((res) => {
          this.locationOptions = res;
          this.isLoading = false;
        });
      this.editModal = true;
    },
    hideEditModal() {
      this.editModal = false;
      this.resetForm();
    },
    hideHistoryModal() {
      this.historyModal = false;
    },
    handleWarehouseChange() {
      this.isLoading = true;
      this.form.storageLocationId = null;
      this.$store
        .dispatch("warehouses/getLocationsByWarehouse", {
          warehouseId: this.form.warehouseId,
          location: "",
        })
        .then((res) => {
          this.locationOptions = res;
          this.isLoading = false;
        });
    },
    addWarehouse() {
      if (!this.form.storageLocationId || this.form.storageLocationId < 0) {
        this.$notify({
          timeout: 2500,
          message: this.$t("wms.messages.requireStorageLocation"),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
        return;
      }

      this.isLoading = true;
      const inputs = Object.assign({}, this.form);
      inputs.purchasePrice = parseFloat(this.form.purchasePrice, 10);
      inputs.additionalCosts = parseFloat(this.form.additionalCosts, 10);
      inputs.reserved = parseInt(this.form.reserved, 10);

      if (!inputs.historyTypeId) {
        this.$notify({
          timeout: 2500,
          message: this.$t("wms.messages.requireHistoryType"),
          icon: "add_alert",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        return;
      }

      axiosFactory()
        .post(`/warehouses/inventories?historyTypeId=${inputs.historyTypeId}`, inputs)
        .then((res) => {
          this.applyFilters({});
        })
        .then(() => {
          this.hideAddModal();
        })
        .catch((err) => {
          //console.log(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    editWarehouse() {
      this.isLoading = true;
      const inputs = Object.assign({}, this.form);
      inputs.date = this.form.date ? formatISO(this.form.date) : null;

      inputs.purchasePrice = parseFloat(this.form.purchasePrice, 10);
      inputs.additionalCosts = parseFloat(this.form.additionalCosts, 10);
      inputs.reserved = parseInt(this.form.reserved, 10);
      inputs.exchangeRate = parseFloat(this.form.exchangeRate, 10);

      axiosFactory()
        .put(`/warehouses/inventories/${inputs.id}?historyTypeId=${inputs.historyTypeId}`, inputs)
        .then((res) => {
          // this.applyFilters({});
          this.$refs.table.refresh();
          this.$notify({
            timeout: 2500,
            message: this.$t("wms.messages.updated"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        })
        .then(() => {
          this.hideEditModal();
        })
        .catch((err) => {
          //console.log(err);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    debounceApplyFilters: debounce(function () {
      this.applyFilters({});
    }, 300),
    handleSelectLocation(value) {
      this.filter.storageLocationId = value?.value ?? undefined;

      this.debounceApplyFilters();
    },
    applyFilters(filters) {
      stickyTableHeader({ selector: "#table-warehouses" });
      this.$refs.table.options.params = {
        ...this.$refs.table.options.params,
        ...this.filter,
        ...filters,
        pageIndex: 1,
      };
      this.currentPage = 1;
      this.$refs.table.setPage(this.currentPage);
    },
    requestFunction(data) {
      const { page, limit, orderBy, ascending, ...filters } = data;
      this.currentPage = page;
      let params = {
        pageSize: limit,
        orderBy,
        ascending,
        ...this.filter,
        ...filters,
        pageIndex: this.currentPage,
      };

      this.isLoading = true;
      return axiosFactory()
        .get(`/warehouses/inventories`, { params })
        .catch(function (e) {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    responseFunction({ data }) {
      this.tableData = data.data.map((i) => ({ ...i, selected: false }));
      this.totalItems = data.total;

      return {
        data: this.tableData,
        count: data.total,
      };
    },
    rowClassCallback(row) {
      return "";
    },
    calcValueOfGoods(row) {
      //return row.inventory * row.additionalCosts + row.purchasePrice * row.exchangeRate * row.inventory;
      return Number(row.purchasePrice) / Number(row.exchangeRate) + Number(row.additionalCosts) + Number(row?.customsItem || 0);
    },
    async addIncomingGoods() {
      const success = await this.$refs.incomingGoodsForm.validate();
      if (!success) {
        this.$notify({
          timeout: 2500,
          message: this.$t("wms.messages.requireAllFields"),
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        return;
      }

      this.isLoading = true;
      const inputs = {};
      inputs.sku = this.form.skuObject?.sku;
      inputs.storageLocationId = this.form.storageLocation.value;
      inputs.inventory = this.form.inventory;
      inputs.reason = this.form.reason;
      inputs.source = this.form.reason;
      inputs.purchasePrice = this.form.purchasePrice;
      inputs.additionalCosts = this.form.additionalCosts;
      inputs.exchangeRate = this.form.exchangeRate;

      axiosFactory()
        .post(`/warehouses`, inputs)
        .then((res) => {
          this.applyFilters({});
          this.$notify({
            timeout: 2500,
            message: this.$t("wms.messages.created"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
        })
        .then(() => {
          this.hideAddModal();
        })
        .catch((err) => {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    fuseSearchSKU(_options, search) {
      const fuse = new Fuse(_options, {
        keys: ["value"],
        shouldSort: true,
        minMatchCharLength: 2,
        includeMatches: true,
        threshold: 0,
      });
      const res = search.length
        ? fuse.search(search).map((pr) => {
            const { item } = pr;
            if (item.sku.toLowerCase() === search.toLowerCase()) {
              return { ...item, exact: true };
            } else {
              return { ...item };
            }
          })
        : fuse.list;
      return res;
    },
    fuseSearch(options, search) {
      const fuse = new Fuse(options, {
        keys: ["title", "value", "sku", "warehouseCode", "storageLocation", "inventory", "reserved"],
        shouldSort: true,
        threshold: 0,
      });
      return search.length ? fuse.search(search).map(({ item }) => item) : fuse.list;
    },
    onSearchInvetories(search, loading) {
      if (search.length) {
        loading(true);
        this.search(loading, search, this);
      }
    },
    search: debounce((loading, search, vm) => {
      axiosFactory()
        .get(`/warehouses/inventories?sku=${search}&pageIndex=0&pageSize=200`)
        .then((res) => {
          // sort data by exact match sku
          const exactMatch = res.data.data.filter((p) => p.sku.toLowerCase() === search.toLowerCase()).map((p) => ({ ...p, exact: true }));
          const notExactMatch = res.data.data.filter((p) => p.sku.toLowerCase() !== search.toLowerCase());
          const sortedData = [...exactMatch, ...notExactMatch];
          vm.options = sortedData.map((p) => ({
            value: p.id,
            ...p,
            name: `${p.sku} (${p.warehouseCode}-${p.storageLocation} | ${vm.$t("wms.inventory")}: <strong>${p.inventory}</strong> | ${vm.$t("wms.reserved")}: ${p.reserved} | ${format(
              Date.parse(p.date),
              "dd.MM.yyyy"
            )})`,
          }));
          loading(false);
        });
    }, 300),
    async addMovementOfGoods() {
      const success = await this.$refs.movementOfGoodsForm.validate();
      if (!success) {
        this.$notify({
          timeout: 2500,
          message: this.$t("wms.messages.requireAllFields"),
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        return;
      }
      this.isLoading = true;
      const inputs = {};
      inputs.storageLocationId = this.form.toStorageLocation.value;
      inputs.quantity = this.form.quantity;
      inputs.warehouseInventoryId = this.form.warehouseInventory.value;

      axiosFactory()
        .post(`/warehouses/moving`, inputs)
        .then((res) => {
          if (res.data.data.status === "error") {
            this.$notify({
              timeout: 2500,
              message: this.$t(`wms.messages.${res.data.data.message}`, res.data.data.metadata),
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error",
            });
            return;
          }

          this.applyFilters({});
          this.$notify({
            timeout: 2500,
            message: this.$t("wms.messages.moved"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          const thiz = this;
          setTimeout(() => {
            thiz.hideAddModal();
          }, 1000);
        })
        .catch((err) => {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    async addManualAdjustment() {
      const success = await this.$refs.manualAdjustmentForm.validate();
      if (!success) {
        this.$notify({
          timeout: 2500,
          message: this.$t("wms.messages.requireAllFields"),
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "error",
        });
        return;
      }

      this.isLoading = true;
      const inputs = {};
      inputs.isAdd = this.form.isAdd;
      inputs.reason = this.form.reason;
      inputs.historyType = this.form.historyType;
      inputs.quantity = this.form.quantity;
      inputs.warehouseInventoryId = this.form.warehouseInventory.value;

      axiosFactory()
        .post(`/warehouses/manual_adjustment`, inputs)
        .then((res) => {
          if (res.data.data.status === "error") {
            const errorCode = res.data.data.message;
            const metadata = res.data.data.metadata;
            this.$notify({
              timeout: 2500,
              message: this.$t(`wms.messages.${errorCode}`, metadata),
              horizontalAlign: "right",
              verticalAlign: "top",
              type: "error",
            });
            return;
          }

          this.applyFilters({});
          this.$notify({
            timeout: 2500,
            message: this.$t("wms.messages.adjusted"),
            icon: "add_alert",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success",
          });
          const thiz = this;
          setTimeout(() => {
            thiz.hideAddModal();
          }, 1000);
        })
        .catch((err) => {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    hasPermission(permissionName, tableName, columnName) {
      return hasPermission(permissionName, tableName, columnName);
    },
    canView(menu) {
      return canView(menu);
    },
    showHistoryModal(sku) {
      this.sku = sku;
      this.currentPage = 1;
      this.historyModal = true;
    },
    dateFilterChanged(d, key) {
      this.historyFilters[key] = d.startDate;
      this.applyHistoryFilters({});
    },
    clearDatetime(key) {
      this.historyFilters[key] = null;
      this.applyHistoryFilters({});
    },
    debounceApplyFiltersHistory: debounce(function () {
      this.applyHistoryFilters();
    }, 300),
    handleSelectHistoryLocation(value) {
      this.historyFilters.storageLocationId = value?.value ?? undefined;
      this.debounceApplyFiltersHistory();
    },
    applyHistoryFilters() {
      const { historyTypeIds, ...rest } = this.historyFilters;
      this.$refs.historyTable.options.params = {
        ...this.$refs.historyTable.options.params,
        ...rest,
        historyTypeIds: historyTypeIds?.join(","),
        pageIndex: 1,
      };
      this.currentPage = 1;
      this.$refs.historyTable.setPage(1);
    },
    historyRequestFunction(data) {
      const { page, limit, orderBy, ascending, pageIndex, ...filters } = data;
      this.currentPage = page;
      let params = {
        pageIndex: page,
        pageSize: limit,
        orderBy,
        ascending,
        ...filters,
      };

      this.isLoading = true;
      return axiosFactory()
        .get(`/warehouses/${this.sku}/histories`, { params })
        .catch(function (e) {})
        .finally(() => {
          this.isLoading = false;
        });
    },
    historyResponseFunction({ data }) {
      console.log(data);
      return {
        data: data.data.items,
        count: data.data.totalItems,
      };
    },
    getI18N(prefix, statusName) {
      return this.$t(`${prefix}.${normalizeStatusName(statusName ?? "")}`);
    },
    exportWarehouses() {
      this.isLoading = true;
      axiosFactory()
        .post(`/warehouses/export_warehouses`)
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  created() {
    this.$store
      .dispatch("warehouses/getLocationsByWarehouse", {
        warehouseId: 0,
        location: "",
        isPulldown: true,
      })
      .then((res) => {
        this.locations = res.filter((loc) => loc.isActive);
      });
    axiosFactory()
      .get("shipping_data/products", {
        params: { keywords: "", pageSize: 10000, includeBWare: true },
      })
      .then((res) => {
        this.products = res.data.data;
      });
    Promise.all([this.$store.dispatch("warehouses/getWarehouses"), this.$store.dispatch("warehouses/getHistoryTypes")]).then(() => {
      this.isLoading = false;
    });
  },
  updated() {
    EventBus.$emit("scroll:changed");
  },
  mounted() {
    EventBus.$on("tabs:panel:changed", () => {
      this.resetForm();
    });
  },
  unmounted() {
    EventBus.$off("tabs:panel:changed");
  },
};
</script>
<style scoped>
.details .md-table-cell {
  text-align: left;
}
.mr-1 {
  margin-right: 15px;
}
.date-title {
  margin-bottom: 0;
  text-align: left;
  font-size: 0.85em;
  color: #888;
}
</style>
